import React, { useEffect, useMemo, useState } from "react";
import { updateProjectProgressSection } from "../../../../api/projectProgressApi";
import {
  calcDurationDays,
  formatDaysWord,
  splitSectionName,
  toDateInputValue,
} from "../projectsScheduleHelpers";
import "../AddWorkTypeModal/AddWorkTypeModal.css";
import "./EditSectionModal.css";

const EditSectionModal = ({
  open,
  projectId,
  section,
  onClose,
  onSaved,
}) => {
  const [planStart, setPlanStart] = useState("");
  const [planEnd, setPlanEnd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !section) return;
    setPlanStart(toDateInputValue(section.plan_start_date));
    setPlanEnd(toDateInputValue(section.plan_end_date));
    setSubmitting(false);
    setError("");
  }, [open, section]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onClose]);

  const sectionLabel = useMemo(() => {
    const { code, title } = splitSectionName(section?.name);
    return title ? `${code} — ${title}` : code;
  }, [section]);

  const duration = calcDurationDays(planStart, planEnd);
  const canSubmit = Boolean(planStart) && Boolean(planEnd) && !submitting;

  if (!open || !section) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!planStart || !planEnd) {
      setError("Укажите дату начала и дату окончания");
      return;
    }
    if (planStart > planEnd) {
      setError("Дата начала не может быть позже даты окончания");
      return;
    }
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setError("");
      const updated = await updateProjectProgressSection(
        projectId,
        section.id,
        {
          plan_start_date: planStart,
          plan_end_date: planEnd,
        },
      );
      onSaved?.(updated);
      onClose();
    } catch (err) {
      setError(err?.message || "Не удалось сохранить плановый срок раздела");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="add-work-type-modal-backdrop"
        onClick={() => !submitting && onClose()}
      />
      <div
        className="add-work-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-section-modal-title"
      >
        <h2 id="edit-section-modal-title" className="add-work-type-modal__title">
          Плановый срок раздела
        </h2>
        <p className="edit-section-modal__subtitle">{sectionLabel}</p>

        <div className="add-work-type-modal__hint">
          <span className="add-work-type-modal__hint-icon" aria-hidden>
            ⚠
          </span>
          <p>
            Плановые сроки видов работ и вех этого раздела можно будет выбрать
            только внутри указанного периода.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="edit-section-modal__dates">
            <div>
              <label
                className="add-work-type-modal__label"
                htmlFor="edit-section-plan-start"
              >
                Дата начала
              </label>
              <input
                id="edit-section-plan-start"
                type="date"
                className="edit-section-modal__input"
                value={planStart}
                max={planEnd || undefined}
                onChange={(e) => {
                  setPlanStart(e.target.value);
                  setError("");
                }}
                disabled={submitting}
                required
              />
            </div>
            <div>
              <label
                className="add-work-type-modal__label"
                htmlFor="edit-section-plan-end"
              >
                Дата окончания
              </label>
              <input
                id="edit-section-plan-end"
                type="date"
                className="edit-section-modal__input"
                value={planEnd}
                min={planStart || undefined}
                onChange={(e) => {
                  setPlanEnd(e.target.value);
                  setError("");
                }}
                disabled={submitting}
                required
              />
            </div>
          </div>

          <div className="edit-section-modal__duration">
            Длительность: {formatDaysWord(duration)}
          </div>

          {error && <p className="add-work-type-modal__error">{error}</p>}

          <div className="add-work-type-modal__actions">
            <button
              type="button"
              className="add-work-type-modal__btn add-work-type-modal__btn--cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-work-type-modal__btn add-work-type-modal__btn--submit"
              disabled={!canSubmit}
            >
              {submitting ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditSectionModal;
